/**
 * Client-side internationalization (i18n) module
 * Manages language selection, UI translations, and communication with server
 */
import { Platform } from './ui/platform.js';
import { emitLanguageChange } from './i18n-events.js';

const i18n = (function () {
    'use strict';

    const STORAGE_KEY = 'moonfall_language';
    const DEFAULT_LANGUAGE = 'en';

    // Supported languages (mirrored from server for client-side display)
    const SUPPORTED_LANGUAGES = {
        'en': { name: 'English', nativeName: 'English', flag: '🇺🇸' },
        'ja': { name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
        'ko': { name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
        'zh-CN': { name: 'Chinese (Simplified)', nativeName: '简体中文', flag: '🇨🇳' },
        'de': { name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
        'fr': { name: 'French', nativeName: 'Français', flag: '🇫🇷' },
        'es': { name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
        'pt': { name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' }
    };

    let currentLanguage = DEFAULT_LANGUAGE;
    const translations = {};
    let isInitialized = false;
    let initPromise = null;
    let closeLanguageSelector = null;
    // Downloads in flight, by language, so a hover warm and the press after it
    // share one request.
    const languageLoads = new Map();
    // The language the latest setLanguage asked for. A switch whose download
    // lands after a later pick must not overwrite that pick.
    let requestedLanguage = null;
    // The whole ui.json, downloaded for an open language list: in flight or
    // landed; cleared when it fails so the next opening tries again
    // (loadEveryLanguage).
    let everyLanguageLoad = null;

    /* An OWN key: `SUPPORTED_LANGUAGES[x]` alone also answered for
       `constructor`, `toString` and every other Object.prototype name. */
    function isSupportedLanguage(code) {
        return typeof code === 'string' && Object.prototype.hasOwnProperty.call(SUPPORTED_LANGUAGES, code);
    }

    /** A supported language code from loose input (`zh_CN`, `fr-CA`, `JA`), or null. */
    function normalizeLanguageCode(raw) {
        if (typeof raw !== 'string' || !raw.trim()) return null;
        const lower = raw.trim().replace(/_/g, '-').toLowerCase();
        if (['zh', 'zh-cn', 'zh-hans', 'zh-sg'].includes(lower)) return 'zh-CN';
        const base = lower.split('-')[0];
        return isSupportedLanguage(base) ? base : null;
    }

    function languageFromUrl() {
        try {
            return normalizeLanguageCode(new URLSearchParams(window.location.search).get('lang'));
        } catch (_) {
            return null;
        }
    }

    function isItchHost() {
        const host = window.location.hostname;
        return host.includes('itch.zone') || host.includes('itch.io');
    }

    // On itch.io, the iframe is served from itch.zone, so we need to fetch from moonfall.io
    function translationsRoot() {
        return isItchHost()
            ? 'https://moonfall.io/translations/'
            : '/translations/';
    }

    /* A LANGUAGE FILE NAMED BY ITS CONTENT (boot plan B2). The page's
       configuration (MoonfallEnv.i18nHashes, inline in the /play head) names
       each language file by the sha256 of its bytes; asked for by that name,
       the server lets the browser keep the file for a year, so a return visit
       reads it from the cache instead of revalidating it. No name — an
       itch.io copy (moonfall.io's names describe moonfall.io's build), a
       page without the configuration, a malformed value — keeps today's
       URL. The head of index.html applies the same rule. */
    function translationName(language) {
        if (isItchHost()) return null;
        try {
            const names = /** @type {any} */ (window).MoonfallEnv?.i18nHashes;
            if (!names || typeof names !== 'object' || !Object.prototype.hasOwnProperty.call(names, language)) return null;
            const name = names[language];
            return typeof name === 'string' && /^[0-9a-f]{16}$/.test(name) ? name : null;
        } catch (_) {
            return null;
        }
    }

    /* ONE LANGUAGE, NOT EIGHT. The server cuts this file from ui.json: the
       language's own copy plus the English `t` falls back to, about a fifth of
       the whole file (game/utils/uiTranslationSlices.js). index.html starts
       this exact URL's download at parse time, resolving the language the way
       init() does below — change one and the other has to follow
       (tests/unit/i18n/translationPreload.test.js). */
    function languageFileUrl(language) {
        const name = translationName(language);
        return `${translationsRoot()}ui.${language}.json${name ? `?h=${name}` : ''}`;
    }

    /** The file a URL names, without its content name: the head and i18n.js ask for the same file. */
    function translationFile(url) {
        return String(url).split('?')[0];
    }

    function hasLanguage(language) {
        return !!translations[language] && !!translations[DEFAULT_LANGUAGE];
    }

    /* THE HEAD'S REQUEST, READ ONCE. index.html starts the player's language
       file with a plain fetch() at parse time and leaves the pending response
       on window.__moonfallTranslationRequest with its URL. The first language
       load takes it: that response when it is for the URL asked for, nothing
       otherwise (an answer for another language is dropped, never read later).
       It used to be a <link rel="preload">, and no preload was both one
       download and the current file in every engine (26 September 2026):
       WebKit downloaded a `crossorigin` one twice, and reused a no-cors one
       WITHOUT validating it, even on a reload while the previous page's copy
       was in memory, so a reload just after a release read the previous
       release's file. Handing the request over has nothing to match.
       It is the same FILE when only the content name differs: a copy whose
       configuration arrived after its head (the plain /env.js request at the
       end of the body) asked by today's URL, and its answer is the current
       file all the same.
       Contract: tests/unit/i18n/translationPreload.test.js. */
    function takeHeadRequest(url) {
        const page = /** @type {any} */ (window);
        const handoff = page.__moonfallTranslationRequest;
        if (!handoff) return null;
        delete page.__moonfallTranslationRequest;
        return translationFile(handoff.url) === translationFile(url) ? handoff.response : null;
    }

    async function fetchTranslationFile(url, headRequest = null) {
        const response = await (headRequest || fetch(url));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (!payload || typeof payload !== 'object') throw new Error('not a translation file');
        return payload;
    }

    function mergeTranslations(payload) {
        for (const [code, strings] of Object.entries(payload)) {
            if (strings && typeof strings === 'object') translations[code] = strings;
        }
    }

    /**
     * Make `language` showable: download its file, or the whole ui.json when
     * that file fails or lacks the language (never keys where words should be).
     * The open list's download of the whole ui.json, when one is on its way,
     * answers too if it brings the language first.
     * Resolves true once one of them has been merged, false only when neither
     * answered. Never rejects.
     */
    function loadLanguage(language) {
        if (hasLanguage(language)) return Promise.resolve(true);
        const pending = languageLoads.get(language);
        if (pending) return pending;
        const own = downloadLanguage(language);
        // The open list's download of every language may bring it first; the
        // pick never waits on that request alone (see EVERY LANGUAGE ONCE THE
        // LIST OPENS).
        const wholeFile = everyLanguageLoad;
        const load = (wholeFile
            ? new Promise((resolve) => {
                wholeFile.then((landed) => { if (landed && hasLanguage(language)) resolve(true); });
                own.then(resolve);
            })
            : own
        ).finally(() => languageLoads.delete(language));
        languageLoads.set(language, load);
        return load;
    }

    async function downloadLanguage(language) {
        try {
            const url = languageFileUrl(language);
            const file = await fetchTranslationFile(url, takeHeadRequest(url));
            if (file[language] && file[DEFAULT_LANGUAGE]) {
                mergeTranslations(file);
                return true;
            }
            console.warn('[i18n] The', language, 'translations are incomplete; loading ui.json instead');
        } catch (e) {
            console.warn('[i18n] Could not load the', language, 'translations; loading ui.json instead:', e?.message || e);
        }
        // The whole file: the open list's download of it when that is already
        // on its way, never a second copy.
        if (await loadEveryLanguage()) return true;
        console.error('[i18n] Error loading translations: neither the', language, 'file nor ui.json loaded');
        return false;
    }

    /* Pointing at a language makes its pick likely. While the open list's
       download of every language is on its way, that brings it and nothing
       else is sent; its own file starts when that download fails, or at once
       when none is on its way. A press is the pick itself and loads at once
       (loadLanguage). */
    function warmLanguage(language) {
        if (!everyLanguageLoad) {
            void loadLanguage(language);
            return;
        }
        void everyLanguageLoad.then((landed) => { if (!landed) void loadLanguage(language); });
    }

    /*
     * EVERY LANGUAGE ONCE THE LIST OPENS.
     *
     * The boot downloads one language, so a pick of any other waited for its
     * file first. Measured 25 September 2026 (phone, 150 ms RTT): the tap on
     * Deutsch changed the words 696 ms after the finger landed on a cold visit
     * and 273-313 ms on a return one (a 304), against 207-254 ms when ui.json
     * came at boot. The file was asked for at the tap, ~1.4 s after the list
     * had opened. Opening the list is when a switch becomes likely and which
     * language it will be is not known yet, so that is when every language
     * downloads: the whole ui.json, one request, fewer bytes than seven
     * language files that would each repeat the English. A pick after it
     * lands applies in the same task, as it did before. A pick (or the press
     * that starts it) while it is still on its way sends its own file as a
     * pick always did, and the first of the two that brings the language
     * applies it (loadLanguage). Waiting on the whole file alone held a pick
     * behind a request it did not make, with no cap: a stalled ui.json kept
     * the page in English for as long as it stalled, and on a slow link
     * (1.6 Mbps, 320 ms) a pick 100 ms after the list opened waited for
     * ~199 KB gzipped instead of its own ~48 KB — words at ~1000 ms instead of
     * ~350 ms (review, 25 September 2026). The two share the link while both
     * are on their way, so a pick that early lands a little later than its
     * file alone would; one made after the whole file landed (the ~1.4 s the
     * meter measured) sends nothing. Pointing at a language sends nothing
     * while the whole file is on its way (warmLanguage). Nothing is applied by
     * the download itself. Only the request is sent here — no work stands
     * between the tap and the list painting.
     */
    function hasEveryLanguage() {
        return Object.keys(SUPPORTED_LANGUAGES).every(hasLanguage);
    }

    function loadEveryLanguage() {
        if (hasEveryLanguage()) return Promise.resolve(true);
        if (everyLanguageLoad) return everyLanguageLoad;
        everyLanguageLoad = fetchTranslationFile(`${translationsRoot()}ui.json`)
            .then((payload) => {
                mergeTranslations(payload);
                return true;
            })
            .catch((e) => {
                console.warn('[i18n] Could not download ui.json:', e?.message || e);
                everyLanguageLoad = null;
                return false;
            });
        return everyLanguageLoad;
    }

    /**
     * Initialize the i18n module
     * Loads translations and applies them to the page
     * @param {{ language?: string, persist?: boolean, selector?: boolean }} [options]
     *   `language` pins this page to one language, ahead of the URL and the
     *   saved preference: a page that shows a fixed-language artefact (the
     *   storybook volume, written in the language its match was played in)
     *   names it here. `persist: false` leaves the saved preference alone,
     *   and `selector: false` mounts no language chooser -- a pinned page has
     *   nothing to choose, and the chooser writes the saved preference.
     */
    async function init(options = {}) {
        if (isInitialized) return Promise.resolve();
        if (initPromise) return initPromise;

        initPromise = (async () => {
            const pinnedLanguage = normalizeLanguageCode(options && options.language);
            // Otherwise a locale-stamped shared URL is authoritative for this
            // visit so the page, share copy, and subsequent CTA stay in one
            // language.
            const linkedLanguage = pinnedLanguage || languageFromUrl();
            if (linkedLanguage) currentLanguage = linkedLanguage;

            // Load saved language preference only when the URL did not choose one.
            try {
                if (linkedLanguage) {
                    if (!(options && options.persist === false)) {
                        localStorage.setItem(STORAGE_KEY, linkedLanguage);
                    }
                } else {
                    const saved = localStorage.getItem(STORAGE_KEY);
                    if (isSupportedLanguage(saved)) currentLanguage = saved;
                }
            } catch (e) {
                console.warn('[i18n] Could not access language in localStorage:', e.message);
            }

            // Fetch this language's translations (already downloading since
            // the head of index.html asked for them: takeHeadRequest)
            await loadLanguage(currentLanguage);
            if (window.CLIENT_DEBUG_MODE) {
                console.log('[i18n] Loaded translations for', Object.keys(translations).length, 'languages');
            }

            // Apply translations to the page (the input mode read before the
            // first write: see applyTranslations)
            const mobileUi = isMobileUiNow();
            document.documentElement.lang = currentLanguage;
            applyTranslations(mobileUi);

            // Create language selector UI
            if (!(options && options.selector === false)) createLanguageSelector();

            isInitialized = true;

            // Notify server of language preference if connected
            notifyServer();

            window.dispatchEvent(new CustomEvent('moonfall:i18nready', {
                detail: { language: currentLanguage }
            }));
        })().catch((err) => {
            initPromise = null;
            throw err;
        });

        return initPromise;
    }

    /**
     * Set the current language. A language not downloaded yet is fetched FIRST
     * and applied in one step when it lands, so the page never shows it
     * half-applied or English in its place; one already downloaded is applied
     * at once. If its copy cannot be downloaded at all the page stays in the
     * language it was in.
     * @param {string} lang - Language code (e.g., 'ja')
     * @param {boolean} notify - Whether to notify the server (default true)
     * @returns {Promise<void>} settles once the switch is applied (or refused)
     */
    function setLanguage(lang, notify = true) {
        if (!isSupportedLanguage(lang)) {
            console.warn('[i18n] Unsupported language:', lang);
            return Promise.resolve();
        }

        requestedLanguage = lang;
        const prepared = prepareLanguage(lang);
        if (hasLanguage(lang) && Array.isArray(prepared)) {
            setSwitchPending(false);
            applyLanguage(lang, notify, prepared);
            return Promise.resolve();
        }

        setSwitchPending(true);
        const loading = hasLanguage(lang) ? Promise.resolve(true) : loadLanguage(lang);
        return Promise.all([loading, Promise.resolve(prepared)]).then(([loaded, applies]) => {
            // A later pick won while this one was downloading.
            if (requestedLanguage !== lang) return;
            setSwitchPending(false);
            if (!loaded) {
                console.error('[i18n] Could not switch to', lang, '- its translations did not load');
                return;
            }
            applyLanguage(lang, notify, applies);
        });
    }

    /*
     * WORDS THAT COME FROM THE SERVER CHANGE WITH THE REST.
     *
     * Some screens print words the server translates (the quest rail's card
     * names). Re-reading them after the switch rebuilt that screen ~0.55 s
     * after every other label had changed (measured 25 September 2026). Such a
     * screen registers a preparer: `preparer(lang)` returns nothing, or a
     * function (or a promise of one) that applies its new-language answer.
     * The switch waits for every preparer — capped at
     * LANGUAGE_PREPARE_CAP_MS, while the language button says it is busy — and
     * runs their appliers in the same task as the rewrite. A preparer that
     * fails or misses the cap is skipped; its screen updates when its own
     * answer lands. With no preparer pending, the switch stays synchronous.
     */
    const LANGUAGE_PREPARE_CAP_MS = 1200;
    const languagePreparers = new Set();

    function registerLanguagePreparer(preparer) {
        if (typeof preparer !== 'function') return () => {};
        languagePreparers.add(preparer);
        return () => languagePreparers.delete(preparer);
    }

    /** Appliers now (an array), or a promise of them once every preparer answered or the cap passed. */
    function prepareLanguage(lang) {
        const answers = [];
        languagePreparers.forEach((preparer) => {
            try {
                answers.push(preparer(lang));
            } catch (error) {
                console.warn('[i18n] A language preparer failed:', error);
            }
        });
        const isPromise = (value) => value && typeof value.then === 'function';
        if (!answers.some(isPromise)) return answers.filter((answer) => typeof answer === 'function');
        return new Promise((resolve) => {
            const appliers = answers.map((answer) => (typeof answer === 'function' ? answer : null));
            let waiting = answers.length;
            let finished = false;
            const finish = () => {
                if (finished) return;
                finished = true;
                clearTimeout(cap);
                resolve(appliers.filter(Boolean));
            };
            const cap = setTimeout(finish, LANGUAGE_PREPARE_CAP_MS);
            answers.forEach((answer, index) => {
                Promise.resolve(answer)
                    .then((value) => { if (!finished) appliers[index] = typeof value === 'function' ? value : null; })
                    .catch((error) => { console.warn('[i18n] A language preparer failed:', error); })
                    .finally(() => { waiting -= 1; if (waiting === 0) finish(); });
            });
        });
    }

    /** The language button says a switch is on its way while its copy downloads. */
    function setSwitchPending(pending) {
        const button = document.getElementById('language-btn');
        if (!button) return;
        if (pending) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
    }

    function applyLanguage(lang, notify, appliers = []) {
        // Before the first write, while layout is still clean (applyTranslations).
        const mobileUi = isMobileUiNow();
        const previousLanguage = currentLanguage;
        currentLanguage = lang;
        document.documentElement.lang = currentLanguage;

        // Save to localStorage
        try {
            localStorage.setItem(STORAGE_KEY, lang);
        } catch (e) {
            console.warn('[i18n] Could not save language to localStorage:', e.message);
        }

        // Apply translations
        applyTranslations(mobileUi);

        // Update language selector display
        updateLanguageSelectorDisplay();

        // Screens whose words come from the server, in this same task.
        appliers.forEach((apply) => {
            try {
                apply();
            } catch (error) {
                console.warn('[i18n] A language applier failed:', error);
            }
        });

        // Notify server
        if (notify) {
            notifyServer();
        }

        if (previousLanguage !== currentLanguage) {
            window.dispatchEvent(new CustomEvent('moonfall:languagechange', {
                detail: {
                    language: currentLanguage,
                    previousLanguage
                }
            }));
        }

        if (window.CLIENT_DEBUG_MODE) {
            console.log('[i18n] Language set to:', lang, 'notify:', notify);
        }
    }

    /**
     * Get the current language code
     * @returns {string}
     */
    function getLanguage() {
        return currentLanguage;
    }

    /**
     * Get a translation for a key
     * @param {string} key - Dot-notation key (e.g., 'lobby.startTutorial')
     * @param {Object} replacements - Optional key-value pairs for interpolation
     * @returns {string}
     */
    function t(key, replacements = {}) {
        const keys = key.split('.');
        let value = translations[currentLanguage];

        // Navigate to the nested value
        for (const k of keys) {
            if (value === undefined) break;
            value = value[k];
        }

        // Fallback to English if not found
        if (value === undefined) {
            value = translations['en'];
            for (const k of keys) {
                if (value === undefined) break;
                value = value[k];
            }
        }

        // Return key if still not found
        if (value === undefined) {
            return key;
        }

        // Handle replacements (e.g., {{name}})
        if (typeof value === 'string' && Object.keys(replacements).length > 0) {
            for (const [placeholder, replacement] of Object.entries(replacements)) {
                value = value.replace(
                    new RegExp(`\\{\\{${placeholder}\\}\\}`, 'g'),
                    () => String(replacement),
                );
            }
        }

        return value;
    }

    function isMobileUiNow() {
        return !!Platform?.isMobileUi?.();
    }

    /* Writes only a word that changes. Setting the same text still replaces
       the node (and the same attribute value still records a mutation), and
       the page is translated again whole on every language change by the
       chrome menu (audio-settings.js) — each of those writes was layout work
       and observer noise for nothing. */
    function setAttributeIfChanged(el, name, value) {
        if (el.getAttribute(name) !== value) el.setAttribute(name, value);
    }

    /**
     * Apply translations to all elements with data-i18n attribute
     */
    function applyTextTranslation(el, mobileUi) {
        if (!el || el.hasAttribute('data-i18n-runtime')) return;
        const mobileKey = el.getAttribute('data-i18n-mobile');
        const key = mobileUi && mobileKey ? mobileKey : el.getAttribute('data-i18n');
        const translated = t(key);
        if (translated !== key && el.textContent !== translated) {
            el.textContent = translated;
        }
    }

    /**
     * @param {boolean} [mobileUi] the input mode, when the caller read it
     *   before its own first write; read here otherwise.
     */
    function applyTranslations(mobileUi) {
        /* ONE VIEWPORT READ PER PASS. Every label used to ask
           `Platform.isMobileUi()`, which reads `window.innerWidth`, right after
           the previous label's text was written — and in Chromium with a
           mobile viewport (every Android phone) that read is a synchronous
           layout. One layout per label: the switch on the phone seat was a
           96-131 ms task at 4x CPU (25 September 2026), and index.html's own
           labels, rewritten twice as a switch does, took 101-118 ms against
           21-31 ms with one read (Chromium, 844x390 mobile, 4x CPU). The
           viewport cannot change inside this synchronous pass, so one read
           answers for all of it. */
        const mobile = typeof mobileUi === 'boolean' ? mobileUi : isMobileUiNow();

        // Text content
        document.querySelectorAll('[data-i18n]').forEach(el => {
            // Composite runtime state (for example "Page 2 of 5" or an
            // authoritative staged price) is translated by the owning module.
            // Reapplying its static HTML key on a viewport change would erase
            // live state and briefly lie to the player.
            applyTextTranslation(el, mobile);
        });

        // Placeholders
        document.querySelectorAll('[data-i18n-placeholder]').forEach(el => {
            const key = el.getAttribute('data-i18n-placeholder');
            const translated = t(key);
            if (translated !== key) {
                setAttributeIfChanged(el, 'placeholder', translated);
            }
        });

        // Titles/tooltips
        document.querySelectorAll('[data-i18n-title]').forEach(el => {
            const key = el.getAttribute('data-i18n-title');
            const translated = t(key);
            if (translated !== key) {
                setAttributeIfChanged(el, 'title', translated);
            }
        });

        // Accessible names for icon-only and otherwise non-text controls.
        document.querySelectorAll('[data-i18n-aria-label]').forEach(el => {
            const key = el.getAttribute('data-i18n-aria-label');
            const translated = t(key);
            if (translated !== key) {
                setAttributeIfChanged(el, 'aria-label', translated);
            }
        });

        /* THE RE-MEASURE SIGNAL. Three modules have listened for this on
           `document` for an unknown length of time and NOTHING has ever
           dispatched it, so the audio control, the tutorial cue geometry and
           the Skip button all stopped re-seating after a language change —
           silently, because every `data-i18n` label repaints above regardless
           and only the measured POSITIONS went stale. German and Japanese
           labels are not the width English ones are.

           It belongs here rather than in setLanguage: this is the moment the
           DOM text has actually changed, and it is the only moment a listener
           measuring against that text can trust. */
        try {
            document.dispatchEvent(new CustomEvent('moonfall:i18n-translated', {
                detail: { language: currentLanguage },
            }));
        } catch (_) { /* a listener throwing must not break the language switch */ }
    }

    function applyViewportTranslations() {
        // Only input-mode wording changes at a breakpoint. Re-scanning every
        // static label is unnecessary and used to overwrite live UI state.
        const mobileUi = isMobileUiNow();
        document.querySelectorAll('[data-i18n-mobile]').forEach(el => applyTextTranslation(el, mobileUi));
    }

    /**
     * Create the language selector UI
     */
    function createLanguageSelector() {
        // Check if already exists
        if (document.getElementById('language-selector')) {
            return;
        }

        const selector = document.createElement('div');
        selector.id = 'language-selector';
        selector.className = 'language-selector';

        const button = document.createElement('button');
        button.id = 'language-btn';
        button.className = 'language-btn';
        button.type = 'button';
        button.setAttribute('aria-haspopup', 'listbox');
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', 'language-dropdown');
        applyLanguageButtonLabel(button, SUPPORTED_LANGUAGES[currentLanguage]);

        const dropdown = document.createElement('div');
        dropdown.id = 'language-dropdown';
        dropdown.className = 'language-dropdown';
        dropdown.setAttribute('role', 'listbox');
        dropdown.setAttribute('aria-labelledby', button.id);
        dropdown.setAttribute('aria-hidden', 'true');
        dropdown.hidden = true;

        let isOpen = false;
        const optionElements = [];

        function focusOption(index) {
            if (!optionElements.length) return;
            const targetIndex = ((index % optionElements.length) + optionElements.length) % optionElements.length;
            optionElements.forEach((option, optionIndex) => {
                option.tabIndex = optionIndex === targetIndex ? 0 : -1;
            });
            optionElements[targetIndex].focus();
        }

        function focusSelectedOption() {
            const selectedIndex = optionElements.findIndex(option => option.getAttribute('aria-selected') === 'true');
            focusOption(selectedIndex >= 0 ? selectedIndex : 0);
        }

        function setOpenState(nextOpen, { focusSelected = false, restoreButtonFocus = false } = {}) {
            isOpen = nextOpen;
            selector.classList.toggle('is-open', isOpen);
            button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            dropdown.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
            dropdown.hidden = !isOpen;

            if (isOpen && focusSelected) {
                focusSelectedOption();
            } else if (!isOpen && restoreButtonFocus) {
                button.focus();
            }

            // A pick is now likely: every language downloads (loadEveryLanguage).
            if (isOpen) void loadEveryLanguage();
        }

        closeLanguageSelector = () => setOpenState(false);

        function toggleOpenState() {
            setOpenState(!isOpen, {
                focusSelected: !isOpen,
                restoreButtonFocus: isOpen
            });
        }

        function selectOption(option) {
            setLanguage(option.dataset.lang);
            setOpenState(false, { restoreButtonFocus: true });
        }

        function onOptionKeydown(event, option) {
            const optionIndex = optionElements.indexOf(option);

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                selectOption(option);
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                setOpenState(false, { restoreButtonFocus: true });
                return;
            }

            let nextIndex = null;
            if (event.key === 'ArrowDown') nextIndex = optionIndex + 1;
            if (event.key === 'ArrowUp') nextIndex = optionIndex - 1;
            if (event.key === 'Home') nextIndex = 0;
            if (event.key === 'End') nextIndex = optionElements.length - 1;

            if (nextIndex !== null) {
                event.preventDefault();
                focusOption(nextIndex);
            }
        }

        // Group languages by region for better UX
        const regions = [
            { key: 'languageSelector.regions.default', fallback: 'Default', codes: ['en'] },
            { key: 'languageSelector.regions.asian', fallback: 'Asian', codes: ['ja', 'ko', 'zh-CN'] },
            { key: 'languageSelector.regions.european', fallback: 'European', codes: ['de', 'fr', 'es', 'pt'] }
        ];

        for (const { key, fallback, codes } of regions) {
            const header = document.createElement('div');
            header.className = 'language-region-header';
            header.setAttribute('role', 'presentation');
            header.setAttribute('data-i18n', key);
            // Built even when ui.json failed to load — a player must still be
            // able to pick a language — and `t` answers a missing key with the
            // key, which printed "languageSelector.regions.default" here.
            const translated = t(key);
            header.textContent = translated !== key ? translated : fallback;
            dropdown.appendChild(header);

            for (const code of codes) {
                const langData = SUPPORTED_LANGUAGES[code];
                const option = document.createElement('div');
                option.className = 'language-option';
                option.dataset.lang = code;
                option.setAttribute('role', 'option');
                option.setAttribute('aria-selected', code === currentLanguage ? 'true' : 'false');
                option.tabIndex = code === currentLanguage ? 0 : -1;
                option.innerHTML = `<span class="language-flag">${langData.flag}</span> <span class="language-name">${langData.nativeName}</span>`;

                if (code === currentLanguage) {
                    option.classList.add('selected');
                }

                option.addEventListener('click', () => selectOption(option));
                option.addEventListener('keydown', event => onOptionKeydown(event, option));
                // Pointing at a language is when its pick becomes likely:
                // start its download then, so the press that follows applies it
                // at once instead of waiting a round trip (warmLanguage). The
                // press is the pick itself: its file starts at the finger, ~100
                // ms before the click (loadLanguage).
                option.addEventListener('pointerenter', () => warmLanguage(code));
                option.addEventListener('pointerdown', () => { void loadLanguage(code); });

                optionElements.push(option);
                dropdown.appendChild(option);
            }
        }

        button.addEventListener('click', () => {
            toggleOpenState();
        });
        button.addEventListener('keydown', event => {
            if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            setOpenState(true);

            if (event.key === 'ArrowUp' || event.key === 'End') {
                focusOption(optionElements.length - 1);
            } else if (event.key === 'Home') {
                focusOption(0);
            } else {
                focusSelectedOption();
            }
        });

        // A listbox must not remain visually/semantically expanded after the
        // user tabs into the rest of the app. Keep the new focus target intact;
        // only explicit Escape/trigger closure should restore trigger focus.
        selector.addEventListener('focusout', event => {
            if (!isOpen) return;
            const nextFocus = event.relatedTarget;
            if (!nextFocus || !selector.contains(nextFocus)) {
                setOpenState(false);
            }
        });

        // Pointer users also expect a conventional outside press to dismiss the
        // menu. pointerdown closes before the destination's click/focus action,
        // without stealing that action back to the language trigger.
        document.addEventListener('pointerdown', event => {
            if (isOpen && !selector.contains(event.target)) {
                setOpenState(false);
            }
        });

        setOpenState(false);

        selector.appendChild(button);
        selector.appendChild(dropdown);
        (document.getElementById('lobby-language-cluster') || document.body)
            .appendChild(selector);
    }

    /**
     * Update the language selector button display
     */
    // Build the language button label with safe DOM nodes (no innerHTML). The
    // native name is wrapped so a phone can hide it (flag + chevron only) and
    // keep the top-right cluster from colliding with the mute toggle.
    //
    // The FLAG is wrapped for the mirror-image reason: inside the chrome menu
    // it is the one mark that paints its own colours, in a panel the owner
    // asked to be a single ink, so `chrome-menu.css` hides it there. It was a
    // bare text node before, which no selector can reach — the dropdown options
    // already wrapped theirs in `.language-flag`, so this also makes the button
    // and its own options consistent.
    function applyLanguageButtonLabel(button, langData) {
        button.textContent = '';
        const flagSpan = document.createElement('span');
        flagSpan.className = 'language-flag';
        flagSpan.setAttribute('aria-hidden', 'true');
        flagSpan.textContent = langData.flag;
        const nameSpan = document.createElement('span');
        nameSpan.className = 'language-btn-name';
        nameSpan.textContent = langData.nativeName;
        button.append(flagSpan, ' ', nameSpan, ' ▼');
    }

    function updateLanguageSelectorDisplay() {
        const button = document.getElementById('language-btn');
        if (button) {
            applyLanguageButtonLabel(button, SUPPORTED_LANGUAGES[currentLanguage]);
        }

        // Update selected class in dropdown
        const dropdown = document.getElementById('language-dropdown');
        if (dropdown) {
            dropdown.querySelectorAll('.language-option').forEach(opt => {
                const isSelected = opt.dataset.lang === currentLanguage;
                opt.classList.toggle('selected', isSelected);
                opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
                opt.tabIndex = isSelected ? 0 : -1;
            });
        }
    }

    /**
     * Notify server of current language preference
     */
    function notifyServer() {
        // The game page's socket subscribes through i18n-events.js. Landing and
        // storybook subscribe nothing and this is a no-op there, which is what
        // the old `if (window.SocketHandler && ...)` probe achieved by accident.
        emitLanguageChange(currentLanguage);
    }

    /**
     * Get list of supported languages
     * @returns {Array}
     */
    function getSupportedLanguages() {
        return Object.entries(SUPPORTED_LANGUAGES).map(([code, data]) => ({
            code,
            ...data
        }));
    }

    function hideSelector() {
        const selector = document.getElementById('language-selector');
        if (!selector) return;
        closeLanguageSelector?.();
        const focused = document.activeElement;
        if (focused && selector.contains(focused) && typeof focused.blur === 'function') {
            focused.blur();
        }
        selector.style.display = 'none';
    }

    function showSelector() {
        const selector = document.getElementById('language-selector');
        if (selector) selector.style.display = 'block';
    }

    // A resize can cross the phone/desktop UI boundary without a language
    // change. Re-apply interaction wording so Click/Tap always matches the
    // active input layout.
    if (!window.__moonfallI18nViewportWired) {
        window.__moonfallI18nViewportWired = true;
        window.addEventListener('moonfall:viewport-change', applyViewportTranslations);
    }

    // Public API
    return {
        registerLanguagePreparer,
        LANGUAGE_PREPARE_CAP_MS,
        init,
        setLanguage,
        getLanguage,
        t,
        applyTranslations,
        translatePage: applyTranslations,
        getSupportedLanguages,
        hideSelector,
        showSelector,
        isInitialized: () => isInitialized
    };
})();

export { i18n };
